import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from "react";
import "./HeroSlides.css";
import EmployeesPanel from "./EmployeesPanel";
import ProjectPanel from "./ProjectPanel";
import { AUTOPLAY_MS, SLIDE_ACCENT, SLIDE_SOLUTION, type SlideId } from "./slides";
import {
  emergerMobileApp,
  marriottLaptopMockup,
  sonderProductMobile,
  veronaHeroTablets,
} from "@/assets";
import { useLocale } from "@/i18n/useLocale";
import { runWhenIdle } from "@/services/gtag";

interface HeroSlidesProps {
  active: number;
  /** Cuantas veces se activo cada slide (se usa para volver a armar su animacion). */
  activations: number[];
  autoPlay: boolean;
  paused: boolean;
  onSelect: (index: number, manual: boolean) => void;
  onHoldChange: (hold: boolean) => void;
}

/**
 * Escenario del hero: tres slides -empleados virtuales, e-commerce y software
 * a medida- y debajo una botonera tipo Apple donde cada pestaña tiene su barra
 * de progreso. La barra es lo que marca el tiempo: cuando termina de llenarse
 * pasa al siguiente (onAnimationEnd), y frenarla -con el mouse o el foco
 * encima- frena el slide, sin timers aparte que sincronizar.
 *
 * Todos los slides estan en el DOM (los inactivos invisibles e `inert`), asi su
 * texto queda en el HTML y las transiciones son solo CSS: los que ya pasaron
 * salen hacia la izquierda y los que vienen entran desde la derecha.
 */
function HeroSlides({ active, activations, autoPlay, paused, onSelect, onHoldChange }: HeroSlidesProps) {
  const { t } = useLocale();
  const slides = t.hero.slides;
  const stage = t.hero.stage;
  const stageRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Las capturas de los slides que todavia no se ven no se piden con el primer
  // pintado: estan en paneles invisibles pero dentro de la distancia del lazy
  // loading, asi que el navegador las bajaba igual (~120 KB) y le sacaban ancho
  // de banda a lo que si esta en pantalla. Se montan cuando la pagina ya cargo y
  // el navegador esta ocioso, o apenas alguien cambia de slide.
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    runWhenIdle(() => setIdle(true));
  }, []);
  const loadAllImages = idle || active !== 0;

  const devices = {
    ecommerce: {
      main: { src: veronaHeroTablets, alt: stage.altVerona, width: 1235, height: 724 },
      side: { src: sonderProductMobile, alt: stage.altSonder, width: 241, height: 352 },
    },
    software: {
      main: { src: marriottLaptopMockup, alt: stage.altMarriott, width: 851, height: 567 },
      side: { src: emergerMobileApp, alt: stage.altEmerger, width: 472, height: 617 },
    },
  };

  function handleTabKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const last = slides.length - 1;
    let next: number | null = null;

    if (e.key === "ArrowRight") next = active === last ? 0 : active + 1;
    else if (e.key === "ArrowLeft") next = active === 0 ? last : active - 1;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = last;

    if (next === null) return;

    e.preventDefault();
    onSelect(next, true);
    tabRefs.current[next]?.focus();
  }

  // Parallax suave: los dispositivos se corren apenas hacia donde mira el
  // mouse. Solo con mouse (en tactil no hay "hover") y se apaga con
  // prefers-reduced-motion desde el CSS.
  function handlePointerMove(e: PointerEvent<HTMLDivElement>) {
    if (e.pointerType !== "mouse") return;

    const el = stageRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    el.style.setProperty("--mx", (((e.clientX - rect.left) / rect.width - 0.5) * 2).toFixed(3));
    el.style.setProperty("--my", (((e.clientY - rect.top) / rect.height - 0.5) * 2).toFixed(3));
  }

  function handlePointerLeave() {
    const el = stageRef.current;
    if (!el) return;

    el.style.setProperty("--mx", "0");
    el.style.setProperty("--my", "0");
  }

  return (
    <div
      className="hero-slides"
      data-paused={paused ? "true" : undefined}
      style={{ "--hs-dur": `${AUTOPLAY_MS}ms` } as CSSProperties}
      onPointerEnter={() => onHoldChange(true)}
      onPointerLeave={() => onHoldChange(false)}
      onFocus={() => onHoldChange(true)}
      onBlur={() => onHoldChange(false)}
    >
      <div
        className="hero-slides__stage"
        ref={stageRef}
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
      >
        {slides.map((slide, index) => {
          const id = slide.id as SlideId;
          const accent = SLIDE_ACCENT[id];
          const isActive = index === active;
          const position = index < active ? "before" : index > active ? "after" : "active";

          return (
            <section
              key={id}
              id={`hero-slide-${id}`}
              role="tabpanel"
              aria-labelledby={`hero-tab-${id}`}
              className="hero-slides__panel"
              data-active={isActive}
              data-pos={position}
              inert={!isActive}
              style={{ "--hue": accent.hue, "--accent": accent.color } as CSSProperties}
            >
              {id === "employees" && (
                <EmployeesPanel run={activations[index]} onInteract={() => onSelect(active, true)} />
              )}

              {id === "ecommerce" && (
                <ProjectPanel
                  variant="ecommerce"
                  showImages={isActive || loadAllImages}
                  run={activations[index]}
                  kicker={stage.ecommerceKicker}
                  main={devices.ecommerce.main}
                  side={devices.ecommerce.side}
                  caseSlugs={["sonder", "verona"]}
                  solutionSlug={SLIDE_SOLUTION.ecommerce}
                />
              )}

              {id === "software" && (
                <ProjectPanel
                  variant="software"
                  showImages={isActive || loadAllImages}
                  run={activations[index]}
                  kicker={stage.softwareKicker}
                  pill={stage.projectsPill}
                  main={devices.software.main}
                  side={devices.software.side}
                  caseSlugs={["riskrator", "crucijuegos"]}
                  solutionSlug={SLIDE_SOLUTION.software}
                />
              )}
            </section>
          );
        })}
      </div>

      <div className="hero-slides__tabs" role="tablist" aria-label={stage.tabsLabel} onKeyDown={handleTabKeyDown}>
        {slides.map((slide, index) => {
          const id = slide.id as SlideId;
          const isActive = index === active;

          return (
            <button
              key={id}
              id={`hero-tab-${id}`}
              ref={(el) => {
                tabRefs.current[index] = el;
              }}
              type="button"
              role="tab"
              className="hero-slides__tab"
              aria-selected={isActive}
              aria-controls={`hero-slide-${id}`}
              tabIndex={isActive ? 0 : -1}
              style={{ "--tab-accent": SLIDE_ACCENT[id].color } as CSSProperties}
              onClick={() => onSelect(index, true)}
            >
              <span className="hero-slides__tab-label">{slide.label}</span>
              <span className="hero-slides__tab-track" aria-hidden="true">
                {isActive && (
                  <span
                    // La key remonta la barra en cada activacion: la animacion
                    // arranca de cero aunque se vuelva al mismo slide.
                    key={activations[index]}
                    className={`hero-slides__tab-fill${autoPlay ? " is-running" : ""}`}
                    onAnimationEnd={() => {
                      if (autoPlay) onSelect((active + 1) % slides.length, false);
                    }}
                  />
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default HeroSlides;
